import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getErrorMessage,
  getErrorSeverity,
  getErrorTitle,
} from "../api/errorMessages";
import { EmailInputForm } from "../components";
import {
  useHealthStatus,
  useProviders,
  useValidateEmailMatrix,
} from "../hooks";

export function ComparePage() {
  const {
    isHealthy,
    isLoading: isHealthLoading,
    isError: healthError,
  } = useHealthStatus();
  const {
    data: providers,
    isLoading: isProvidersLoading,
    isError: providersError,
  } = useProviders();
  const matrixMutation = useValidateEmailMatrix();

  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);

  useEffect(() => {
    if (!providers || hasInitializedSelection) {
      return;
    }

    setSelectedProviderIds(providers.map((provider) => provider.providerId));
    setHasInitializedSelection(true);
  }, [providers, hasInitializedSelection]);

  const handleProviderToggle = useCallback((providerId: string) => {
    setSelectedProviderIds((previous) => {
      if (previous.includes(providerId)) {
        return previous.filter((id) => id !== providerId);
      }
      return [...previous, providerId];
    });
  }, []);

  const handleSelectAllProviders = useCallback(() => {
    if (!providers) {
      return;
    }
    setSelectedProviderIds(providers.map((provider) => provider.providerId));
  }, [providers]);

  const handleClearProviders = useCallback(() => {
    setSelectedProviderIds([]);
  }, []);

  const handleValidate = (email: string) => {
    matrixMutation.mutate(email);
  };

  const isApiUnavailable = !isHealthLoading && (healthError || !isHealthy);
  const isProviderSelectionUnavailable = isProvidersLoading || providersError;
  const isMatrixFormDisabled =
    isApiUnavailable ||
    isProviderSelectionUnavailable ||
    selectedProviderIds.length === 0;

  const filteredResults = useMemo(() => {
    const allResults = matrixMutation.data?.results ?? [];
    if (selectedProviderIds.length === 0) {
      return [];
    }

    const selectedSet = new Set(selectedProviderIds);
    return allResults.filter((result) => selectedSet.has(result.providerId));
  }, [matrixMutation.data, selectedProviderIds]);

  const validCount = filteredResults.filter((result) => result.isValid).length;
  const invalidCount = filteredResults.length - validCount;
  const totalReturned = matrixMutation.data?.results.length ?? 0;

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        Compare
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Validate against all providers, then filter to the providers you care
        about.
      </Typography>

      {isApiUnavailable && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>API Unavailable</AlertTitle>
          The validation API is currently unavailable. Please try again later.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <EmailInputForm
          onSubmit={handleValidate}
          isLoading={matrixMutation.isPending}
          disabled={isMatrixFormDisabled}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6">Provider Selection</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedProviderIds.length} selected
              {providers ? ` of ${providers.length}` : ""}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAllProviders}
              disabled={!providers || providers.length === 0}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearProviders}
              disabled={selectedProviderIds.length === 0}
            >
              Clear
            </Button>
          </Stack>
        </Box>

        {isProvidersLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {providersError && (
          <Alert severity="error">
            <AlertTitle>Unable to load providers</AlertTitle>
            Provider filtering is unavailable until providers can be loaded.
          </Alert>
        )}

        {!isProvidersLoading && !providersError && providers && (
          <FormGroup
            sx={{
              maxHeight: 280,
              overflow: "auto",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 0.5,
            }}
          >
            {providers.map((provider) => (
              <FormControlLabel
                key={provider.providerId}
                control={
                  <Checkbox
                    checked={selectedProviderIds.includes(provider.providerId)}
                    onChange={() => handleProviderToggle(provider.providerId)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography variant="body2">
                      {provider.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({provider.providerId})
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        )}
      </Paper>

      {matrixMutation.isError && (
        <Alert severity={getErrorSeverity(matrixMutation.error)} sx={{ mb: 3 }}>
          <AlertTitle>{getErrorTitle(matrixMutation.error)}</AlertTitle>
          {getErrorMessage(matrixMutation.error)}
        </Alert>
      )}

      {matrixMutation.isSuccess && matrixMutation.data && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ mr: 1 }}>
              Comparison Results
            </Typography>
            <Chip label={`Shown: ${filteredResults.length}/${totalReturned}`} />
            <Chip
              label={`Valid: ${validCount}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Invalid: ${invalidCount}`}
              color="error"
              variant="outlined"
            />
          </Box>

          {matrixMutation.data.universalFailures.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Universal Failures</AlertTitle>
              {matrixMutation.data.universalFailures
                .map((failure) => failure.message)
                .join(" | ")}
            </Alert>
          )}

          {selectedProviderIds.length === 0 && (
            <Alert severity="info">
              Select at least one provider to view comparison results.
            </Alert>
          )}

          {selectedProviderIds.length > 0 && filteredResults.length === 0 && (
            <Alert severity="info">
              No comparison results match the selected providers.
            </Alert>
          )}

          {filteredResults.length > 0 && (
            <Stack spacing={1.5}>
              {filteredResults.map((result) => (
                <Paper key={result.providerId} variant="outlined" sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        {result.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {result.providerId}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {result.isDetected && (
                        <Chip label="Detected" color="info" size="small" />
                      )}
                      <Chip
                        label={
                          result.isApplicable ? "Applicable" : "Not Applicable"
                        }
                        color={result.isApplicable ? "default" : "warning"}
                        size="small"
                      />
                      <Chip
                        label={result.isValid ? "Valid" : "Invalid"}
                        color={result.isValid ? "success" : "error"}
                        size="small"
                      />
                    </Stack>
                  </Box>

                  {result.failures.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      {result.failures.map((failure) => (
                        <Typography
                          key={`${result.providerId}-${failure.ruleId}`}
                          variant="body2"
                          color="text.secondary"
                        >
                          • {failure.message}
                        </Typography>
                      ))}
                    </>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
}
